import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import searchClient from 'bl/client/searchClient.bl';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import IN from 'constants/in';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectHideClientDetails } from 'selectors/clientPermissions.selectors';
import { selectCurrentSalonClientsArray } from 'selectors/currentSalon.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToInitials from 'utils/converters/string/fromAnyToInitials.util';
import { fromUserNameToFirstName } from 'utils/converters/string/fromUserNameToFirstName.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import { capitalizeWords } from 'utils/strings/manipulate.util';

import HeaderWrapper from 'booking/components/common/bw-header/HeaderWrapper';
import BWContent from 'booking/components/common/BWContent';
import ClientItem from 'scheduler/components/ClientItem';

import CN from './ClientSearch.module.scss';

const SHOWN_ITEMS_NUMBER = 1000;

export const ClientSearch = ({ history, salonId, location, setSearchOpen }) => {
  const [searchText, setSearchText] = useState('');
  const [warningText, setWarningText] = useState('');
  const [searchContainer, setSearchContainer] = useState(false);

  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const clientList = useSelector(selectCurrentSalonClientsArray);
  const hideDetails = useSelector(selectHideClientDetails);

  const trimmedSearchText = fromAnyToTrimmed(searchText).replace(/^0+/, '');
  const segments = history.location.pathname.split('/');

  const foundClients = useMemo(() => searchClient({ text: searchText, list: clientList }), [clientList, searchText]);

  const clients = foundClients.length > SHOWN_ITEMS_NUMBER ? foundClients.slice(0, SHOWN_ITEMS_NUMBER) : foundClients;

  const isClientsFull = isFullArray(clients);

  const newPath = useMemo(() => {
    if (segments[3] === 'day') return segments.slice(0, 5).join('/');
    if (segments[3] === 'week') return segments.slice(0, 6).join('/');
    return location.pathname;
  }, [segments, location.pathname]);

  const onBlurSearch = () => setWarningText('');

  const onChangeSearch = useCallback(
    (e) => {
      const value = capitalizeWords(e.target.value);
      setSearchText(value);

      const cleanText = value.trim().replace(/^0+/, '');
      const resultList = searchClient({ text: cleanText, list: clientList });

      if (resultList.length === 0) {
        setWarningText(t('scheduler.clientSearch.noClientsFoundMsg'));
      } else {
        setWarningText('');
      }
    },
    [clientList, t],
  );

  const openSearch = useCallback(() => {
    setSearchText('');
    setWarningText('');
    setSearchContainer((prev) => !prev);
    setSearchOpen((prev) => !prev);
  }, [setSearchOpen]);

  const viewAllClients = useCallback(() => {
    setSearchText('');
    setSearchContainer(false);
    setSearchOpen(false);

    history.push(`/salon/${salonId}/communications/clients/list`);
  }, [history, salonId, setSearchOpen]);

  const addNewClient = useCallback(() => {
    setSearchText('');
    setSearchContainer(false);
    setWarningText('');
    setSearchOpen(false);

    history.push(`${newPath}/create/new/client/new/${trimmedSearchText}`);
  }, [history, newPath, setSearchOpen, trimmedSearchText]);

  const handleClientItem = useCallback(
    (id) => {
      setSearchText('');
      setSearchContainer(false);
      setSearchOpen(false);

      if (id) {
        history.push(`${newPath}/client/${id}`);
      }
    },
    [history, newPath, setSearchOpen],
  );

  return (
    <div
      className={cn({
        'general-search is-open': searchContainer,
        'general-search is-close': !searchContainer,
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <Button tag="SchedulerHeader__today--gensearch" variant="chameleon" noMargin width="auto" onClick={openSearch}>
        <div className={CN.buttonInnerSpace}>
          <span className={CN.space}>
            <Icon variant="users" width="lg" />
          </span>
          {t('scheduler.clientSearch.clientsSearchButton')}
        </div>
      </Button>

      {searchContainer ? (
        <div className={searchContainer ? 'BookingWindow animated slideInRight shadow' : 'BookingWindow animated fadeOut'}>
          <HeaderWrapper>
            <div data-bem="ClientSearch__header" className={CN.headerSearchPanel}>
              <input
                type="text"
                className="input-default search-dropdown"
                placeholder={t('scheduler.clientSearch.searchInputPlaceholder', { clients: clientList.length })}
                autoFocus
                autoComplete="off"
                id="InputSearch"
                onBlur={onBlurSearch}
                value={searchText.replace(/\s{2,}/g, ' ')} // disable extra spaces between names
                onChange={onChangeSearch}
              />
              <div data-bem="ClientSearch__close-button" role="button" tabIndex={0} onClick={openSearch} className={CN.closeButtonContainer}>
                <Icon variant="closeDark" width="lg" />
              </div>
            </div>
          </HeaderWrapper>
          <BWContent additionalClass="">
            <div className={CN.buttonsContainer}>
              {warningText}

              <Button variant="slim" onClick={addNewClient}>
                <span className={CN.buttonText}>{t('scheduler.clientSearch.addNewClients')}</span>
              </Button>
              <div className={CN.gap}>
                {isClientsFull ? (
                  <div />
                ) : (
                  <div className="cfix relative">
                    <Button variant="slim" onClick={viewAllClients}>
                      <span className={CN.buttonText}>{t('scheduler.clientSearch.viewAllClients')}</span>
                    </Button>
                    <TooltipForReceptionist where={IN.viewAllClients} />
                  </div>
                )}
              </div>
              <NavLink to={`/salon/${salonId}/communications/mergeclients`} activeClassName="active">
                <span className={CN.linkText}>{t('scheduler.clientSearch.findMergeDuplicates')}</span>
              </NavLink>
            </div>

            {isClientsFull && searchText.length > 0 && (
              <div>
                <div className="search-dropdown-results find-client">
                  {clients.map((user) => (
                    <ClientItem
                      key={user.id}
                      onClick={() => handleClientItem(user.id)}
                      initials={fromAnyToInitials(user?.user_name)}
                      known_as={user.known_as ? user.known_as : ''}
                      phone={user.phone}
                      notes={user.notes ? t('scheduler.clientSearch.notes', { notes: user.notes }) : ''}
                      rank={user.rank}
                      user_name={hideDetails ? fromUserNameToFirstName(user.user_name) : user.user_name}
                    >
                      {!hideDetails && <td>{user.phone}</td>}
                    </ClientItem>
                  ))}
                </div>
              </div>
            )}
          </BWContent>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
};
